import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/UI/Layouts";
import Content from "../../components/UI/Layouts/Content";
import {
  MultimediaCategory,
  ProgramCategory,
  EditMenuLink,
  MenuLink,
  MenuLinkIcon,
} from "../../types";
import PageHeader from "../../components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { useStores } from "../../hooks/useStores";
import { VirtualizedTable } from "../../components/common/VirtualizedTable";
import { Breadcrumb, BreadcrumbItem } from "../../components/UI/Breadcrumb";
import { useSnackbar } from "notistack";
import { observer } from "mobx-react";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";
import DeleteIcon from "@mui/icons-material/Delete";
import EmptySpace from "../../components/common/EmptySpace";
import Loading from "../../components/common/Loading";
import MenuLinkModal from "../../components/ScreenMenuLink/MenuLink/MenuLinkModal/MenuLinkModal";
import { Image } from "../../components/UI/Image";
import EditIcon from "@mui/icons-material/Edit";

const MenuLinks: React.FC = observer(() => {
  const { categoryStore, configStore } = useStores();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [menuLink, setMenuLink] = useState<MenuLink | undefined>(undefined);

  const modalHandler = useCallback(() => {
    if (isModalOpen) {
      setMenuLink(undefined);
    }
    setIsModalOpen(!isModalOpen);
  }, [isModalOpen]);

  const fetchMenuLinksList = useCallback(async () => {
    await configStore.fetchMenuLinksList().catch((err) => {
      enqueueSnackbar(err.message, {
        variant: "error",
      });
    });
  }, [enqueueSnackbar, configStore]);

  const handleSubmit = useCallback(
    async (data: EditMenuLink) => {
      try {
        data.iconId = data.iconId
          ? data.iconId
          : data.icon
          ? data.icon.icon.id
          : undefined;
        data.categoryId = data.category
          ? typeof data.category === "number"
            ? data.category
            : data.category.id
            ? typeof data.category.id === "number"
              ? data.category.id
              : parseInt(data.category.id.toString())
            : undefined
          : undefined;
        data.multimediaCategoryId = data.multimediaCategory
          ? typeof data.multimediaCategory === "number"
            ? data.multimediaCategory
            : data.multimediaCategory.id
            ? typeof data.multimediaCategory.id === "number"
              ? data.multimediaCategory.id
              : parseInt(data.multimediaCategory.id.toString())
            : undefined
          : undefined;
        delete data.category;
        delete data.multimediaCategory;
        if (menuLink) {
          await configStore.updateMenuLink(data);
        } else {
          await configStore.addMenuLink(data);
        }
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }

      modalHandler();
    },
    [enqueueSnackbar, configStore, modalHandler, t, menuLink]
  );

  const handleDeleteMenuLink = useCallback(
    async (id: number) => {
      try {
        await configStore.deleteMenuLink(id);
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar, configStore, t]
  );

  const columns = useMemo(
    () => [
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.menuLink.column.icon"),
        dataKey: "icon",
        render: (icon: MenuLinkIcon) => {
          return icon ? (
            <Image
              src={icon.icon.iconUrl}
              alt={icon.icon.iconUrl}
              size={50}
              width={50}
              fit={"contain"}
              align={"left"}
            />
          ) : (
            <>-</>
          );
        },
      },
      {
        width: 80,
        flexGrow: 2,
        label: t("screens.menuLink.column.name"),
        dataKey: "name",
        sorter: (a: { name: string }, b: { name: any }) =>
          a.name.localeCompare(b.name),
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.menuLink.column.onClickUrl"),
        dataKey: "onClickUrl",
        render: (onClickUrl: string) => (
          <a
            href={onClickUrl}
            title={onClickUrl}
            target={"_blank"}
            rel={"noopener noreferrer"}
          >
            {onClickUrl}
          </a>
        ),
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.menuLink.column.category"),
        dataKey: "category",
        render: (_: ProgramCategory | MultimediaCategory, menuLink: MenuLink) =>
          menuLink.category ? (
            <span>{menuLink.category.name}</span>
          ) : menuLink.multimediaCategory ? (
            <span>{menuLink.multimediaCategory.name}</span>
          ) : (
            <>-</>
          ),
      },
      {
        width: 90,
        flexGrow: 0,
        label: t("common.action"),
        dataKey: "id",
        render: (id: number, menuLink: MenuLink) => {
          return (
            <>
              <IconButton
                key={id}
                onClick={() => {
                  setMenuLink(menuLink);
                  modalHandler();
                }}
              >
                <EditIcon />
              </IconButton>
              <PopupState variant="popover" popupId="demo-popup-popover">
                {(popupState) => (
                  <div>
                    <IconButton color="error" {...bindTrigger(popupState)}>
                      <DeleteIcon />
                    </IconButton>
                    <Popover
                      {...bindPopover(popupState)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                    >
                      <Typography sx={{ p: 1 }}>
                        {t("common.deletePopUp")}
                        <IconButton
                          color="error"
                          sx={{ p: 0 }}
                          onClick={() => handleDeleteMenuLink(id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Typography>
                    </Popover>
                  </div>
                )}
              </PopupState>
            </>
          );
        },
      },
    ],
    [handleDeleteMenuLink, modalHandler, t]
  );

  useEffect(() => {
    if (!categoryStore.programCategories.length) {
      categoryStore.getProgramCategories();
    }
    if (!categoryStore.multimediaCategories.length) {
      categoryStore.getMultimediaCategories();
    }

    configStore.fetchIconsList();
    fetchMenuLinksList();
  }, [categoryStore, fetchMenuLinksList, configStore]);

  return (
    <Layout hideFooter>
      <Content borderBottomWidth="1px" align="top">
        <Breadcrumb my={"20px"}>
          <BreadcrumbItem path="/">
            {t("screens.menuLink.breadcrumb.home")}
          </BreadcrumbItem>
          <BreadcrumbItem active>
            {t("screens.menuLink.breadcrumb.menuLinks")}
          </BreadcrumbItem>
        </Breadcrumb>

        <PageHeader
          title={t("screens.menuLink.pageHeader.title")}
          buttonEnable={true}
          buttonText={t("screens.menuLink.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      </Content>
      {configStore.isLoading || categoryStore.isLoading ? (
        <Loading />
      ) : configStore.menuLinksList.length > 0 ? (
        <Content fullHeight align="top" mt={[null, null, null, "0px"]}>
          <div
            style={{
              flex: "1 1 auto",
              width: "100%",
              height: `${(configStore.menuLinksList.length + 1) * 50}px`,
              display: "flex",
              marginTop: "20px",
            }}
          >
            <VirtualizedTable
              rowCount={configStore.menuLinksList.length}
              rowGetter={({ index }) => configStore.menuLinksList[index]}
              columns={columns}
              rowStyle={(row) =>
                row.index % 2 === 0 ? { background: "#EDF1FB" } : {}
              }
            />
          </div>
        </Content>
      ) : (
        <EmptySpace
          title={t("screens.menuLink.emptyData")}
          buttonEnable={true}
          buttonText={t("screens.menuLink.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      )}

      <MenuLinkModal
        menuLink={menuLink}
        isSubmitting={false}
        isOpen={isModalOpen}
        onClose={modalHandler}
        onSubmit={handleSubmit}
      />
    </Layout>
  );
});

export default MenuLinks;
